// Page-chrome behaviour shared by site mode (wrapHtmlPage) and standalone
// mode (buildStandaloneHtml). Each init is a no-op when the markup it
// targets is absent — that's how the same module works for both.

export function initKatex() {
    if (typeof window === 'undefined' || typeof window.renderMathInElement !== 'function') return;
    const root = document.getElementById('ph-content') || document.body;
    if (!root) return;
    window.renderMathInElement(root, {
        delimiters: [
            { left: '\\(', right: '\\)', display: false },
            { left: '\\[', right: '\\]', display: true },
        ],
        throwOnError: false,
    });
}

// A heading's visible text: KaTeX keeps a MathML copy of every formula next
// to the rendered one, and footnote markers are not part of the title.
function headingText(h) {
    const clone = h.cloneNode(true);
    clone.querySelectorAll('.katex-mathml, .footnote-ref, .anchor').forEach((el) => el.remove());
    return (clone.textContent || '').replace(/\s+/g, ' ').trim();
}

export function initToc() {
    const toc = document.getElementById('ph-toc');
    if (!toc) return;
    const headings = Array.from(document.querySelectorAll('#ph-content h1, #ph-content h2, #ph-content h3, #ph-content h4'));
    if (headings.length === 0) {
        toc.innerHTML = '<p class="ph-muted-text">No headings.</p>';
        return;
    }
    const usedIds = new Set();
    headings.forEach((h) => {
        if (!h.id) {
            const base = (h.textContent || 'section').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'section';
            let id = base;
            let n = 2;
            while (usedIds.has(id) || document.getElementById(id)) {
                id = `${base}-${n}`;
                n++;
            }
            h.id = id;
        }
        usedIds.add(h.id);
    });
    // Links are built as nodes: heading text that looks like markup
    // (`<us>` in inline code) used to be parsed back into HTML here.
    toc.textContent = '';
    headings.forEach((h) => {
        const a = document.createElement('a');
        a.href = `#${h.id}`;
        a.style.marginLeft = `${Math.max(0, parseInt(h.tagName.slice(1), 10) - 1) * 0.75}rem`;
        a.textContent = headingText(h);
        toc.appendChild(a);
    });

    const tocLinks = Array.from(toc.querySelectorAll('a'));
    if (tocLinks.length === 0) return;

    // IntersectionObserver-driven scroll-spy. Observation zone is the top
    // 30% of the viewport (rootMargin shrinks the root from the bottom).
    // The wide zone makes the spy fire as a heading enters the upper
    // third — the user doesn't have to scroll past all of the previous
    // section before the next one lights up. Each heading is in one of
    // two states tracked in `visibility`:
    //   'above'  — already scrolled past (top < zone) OR currently in zone
    //   'below'  — not yet reached (still under zone)
    // Active link = the last heading whose state is 'above'.
    if (typeof IntersectionObserver === 'undefined') {
        // Pre-2017 browser: fall back to the simple scroll-loop variant.
        const updateSpy = () => {
            const threshold = 90;
            let activeIdx = 0;
            for (let i = 0; i < headings.length; i++) {
                if (headings[i].getBoundingClientRect().top <= threshold) activeIdx = i;
            }
            tocLinks.forEach((a, i) => a.classList.toggle('ph-toc-active', i === activeIdx));
        };
        window.addEventListener('scroll', updateSpy, { passive: true });
        updateSpy();
        return;
    }

    const visibility = new Map();
    const setActive = () => {
        let activeIdx = 0;
        for (let i = 0; i < headings.length; i++) {
            if (visibility.get(headings[i]) === 'above') activeIdx = i;
        }
        tocLinks.forEach((a, i) => a.classList.toggle('ph-toc-active', i === activeIdx));
    };
    const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
            if (entry.isIntersecting) {
                // Heading is currently in the top observation zone.
                visibility.set(entry.target, 'above');
            } else if (entry.boundingClientRect.top < 0) {
                // Exited the zone going up — heading is now above the viewport.
                visibility.set(entry.target, 'above');
            } else {
                // Exited going down (or never entered) — heading is below.
                visibility.set(entry.target, 'below');
            }
        }
        setActive();
    }, { rootMargin: '0px 0px -70% 0px', threshold: 0 });
    headings.forEach((h) => observer.observe(h));
}

export function initMobileNav() {
    const toggle = document.getElementById('ph-nav-toggle');
    const nav = document.getElementById('ph-nav');
    if (!toggle || !nav) return;
    toggle.addEventListener('click', () => {
        const open = nav.classList.toggle('ph-nav-open');
        toggle.setAttribute('aria-expanded', String(open));
    });
}

export function initCopyButtons() {
    // Mermaid diagrams are <pre> too, but a copy button appended into one adds
    // the word "Copy" to its textContent — which is the diagram source mermaid
    // is about to parse, and it fails on it.
    document.querySelectorAll('#ph-content pre:not(.mermaid)').forEach((pre) => {
        const btn = document.createElement('button');
        btn.className = 'ph-copy-btn';
        btn.textContent = 'Copy';
        btn.setAttribute('aria-label', 'Copy code');
        btn.addEventListener('click', () => {
            const code = pre.querySelector('code');
            const text = (code || pre).innerText;
            (navigator.clipboard ? navigator.clipboard.writeText(text) : Promise.reject())
                .then(() => {
                    btn.textContent = 'Copied!';
                    btn.classList.add('ph-copied');
                    setTimeout(() => {
                        btn.textContent = 'Copy';
                        btn.classList.remove('ph-copied');
                    }, 2000);
                })
                .catch(() => { btn.textContent = 'Copy'; });
        });
        pre.appendChild(btn);
    });
}

export function initBackToTop() {
    const backtop = document.getElementById('ph-backtop');
    if (!backtop) return;
    window.addEventListener('scroll', () => {
        backtop.classList.toggle('ph-visible', window.scrollY > window.innerHeight * 0.8);
    }, { passive: true });
    backtop.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    });
}

// Gated on body[data-ph-live-reload] so it only runs in dev-served pages.
export function initLiveReload() {
    if (typeof EventSource === 'undefined') return;
    if (!document.body || !document.body.hasAttribute('data-ph-live-reload')) return;
    const es = new EventSource('/_/reload');
    es.addEventListener('reload', () => location.reload());
}
